/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ActionItem } from '../action-item/ActionItem';
import { VIEW_TYPE } from '../types';
import WorkItemNode from '../work-item-node/WorkItemNode';

import type { WorkGroupProps } from './types';

const noWorkMessage = defineMessage({
	id: 'capacity-planning.work-view.no-work-group.non-final',
	defaultMessage: 'No work item',
	description: 'Header label for the group of actions that have no associated work item.',
});

const expandWorkGroupMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.capacity-planning.work-view.WorkGroup.expandWorkGroup',
	defaultMessage: 'Expand work group',
	description: 'Accessible label for the button that expands a work item group.',
});

const collapseWorkGroupMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.capacity-planning.work-view.WorkGroup.collapseWorkGroup',
	defaultMessage: 'Collapse work group',
	description: 'Accessible label for the button that collapses a work item group.',
});

const styles = cssMap({
	gridContainer: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 32px',
		alignItems: 'center',
		rowGap: 0,
	},
	headerRow: {
		gridColumn: '1 / -1',
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 32px',
		alignItems: 'center',
		width: '100%',
	},
	contentCell: {
		gridColumn: 1,
		gridRow: 1,
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'hidden',
		paddingBlock: token('space.100'),
		paddingRight: token('space.050'),
		pointerEvents: 'none',
		zIndex: 1,
	},
	toggleButton: {
		gridColumn: '1 / -1',
		gridRow: 1,
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 32px',
		alignItems: 'center',
		width: '100%',
		minWidth: '0px',
		paddingBlock: token('space.100'),
		paddingInline: token('space.0'),
		textAlign: 'left',
		backgroundColor: 'transparent',
		border: 'none',
	},
	toggleIcon: {
		gridColumn: 2,
		display: 'flex',
		justifyContent: 'center',
		color: token('color.text.subtle'),
	},
	actionsWrapper: {
		gridColumn: '1 / -1',
		display: 'flex',
		flexDirection: 'column',
		paddingLeft: token('space.025'),
	},
	groupTitleText: {
		minWidth: '0px',
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export function WorkGroup({
	group,
	defaultExpanded = false,
	selectedKeys,
	onSelectionChange,
	workById,
	hydratedUsers,
	onConfirm,
	onCancel,
	getStatus,
}: WorkGroupProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const firstPayload = group.actions.find((a) => a.data != null)?.data ?? null;
	const workItemNode = firstPayload ? (
		<WorkItemNode payload={firstPayload} workById={workById} isGroupHeader />
	) : null;
	const groupTitle = workItemNode ?? (
		<Box xcss={styles.groupTitleText}>
			<Text size="medium" weight="bold">
				{formatMessage(noWorkMessage)}
			</Text>
		</Box>
	);

	const handleToggle = () => setIsExpanded((e) => !e);

	return (
		<Box xcss={styles.gridContainer}>
			<Box xcss={styles.headerRow}>
				<Box xcss={styles.contentCell}>{groupTitle}</Box>
				<Pressable
					xcss={styles.toggleButton}
					onClick={handleToggle}
					aria-expanded={isExpanded}
					aria-label={formatMessage(isExpanded ? collapseWorkGroupMessage : expandWorkGroupMessage)}
				>
					<Box xcss={styles.toggleIcon}>
						{isExpanded ? (
							<ChevronDownIcon label="" size="small" />
						) : (
							<ChevronRightIcon label="" size="small" />
						)}
					</Box>
				</Pressable>
			</Box>
			{isExpanded && (
				<Box xcss={styles.actionsWrapper}>
					{group.actions
						.filter((a) => a.data != null)
						.map((action) => (
							<ActionItem
								key={action.invocationId}
								action={action}
								payload={action.data}
								isChecked={selectedKeys.has(action.invocationId)}
								onToggle={(checked) => onSelectionChange(action.invocationId, checked)}
								workById={workById}
								hydratedUsers={hydratedUsers}
								viewType={VIEW_TYPE.WORK}
								onConfirm={onConfirm}
								onCancel={onCancel}
								getStatus={getStatus}
							/>
						))}
				</Box>
			)}
		</Box>
	);
}
